import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, CaretUpDown, SignOut, Moon, Sun } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTheme } from '@/lib/useTheme';
import { listMyOrganizations } from '@/api/orgs';
import { OrgInitial } from './OrgInitial';
import { SidebarNav } from './SidebarNav';
import type { RecentNav } from './useRecentNav';

interface Props {
  orgName: string;
  slug: string;
  logoKey?: string | undefined;
  // True when the sidebar is in icon-rail mode (desktop only). Mobile sheet
  // always passes `false` since it's already an overlay.
  collapsed: boolean;
  // Recent + pinned nav state, owned by DashboardShell.
  recentNav: RecentNav;
  // Opens the SettingsPanel slide-over. Forwarded to SidebarNav so the
  // Settings row in the "All pages" accordion can toggle it.
  onOpenSettings: () => void;
  // Closes the parent (used inside the mobile sheet so picking an item also
  // dismisses the sheet). Desktop variant passes undefined.
  onNavigate?: (() => void) | undefined;
}

// SidebarContent renders the inside of the sidebar — org header (logo + name
// + city + switch action), the primary nav, and a theme-toggle footer.
// The collapse/expand control lives on the aside's edge in DashboardShell,
// not here, so this component stays the same shape in the desktop rail and
// the mobile sheet. User identity moved to the dropdown ("signed in as …")
// so the secondary line under the org name can carry the city — that's
// useful disambiguation when the same name maps to multiple clubs.
export function SidebarContent({
  orgName,
  slug,
  logoKey,
  collapsed,
  recentNav,
  onOpenSettings,
  onNavigate,
}: Props) {
  const auth = useAuth();
  const { t } = useTranslation('common');
  const { theme, toggle: toggleTheme } = useTheme();
  const token = auth.user?.access_token ?? null;

  // Best effort: prefer the OIDC profile name; fall back to email; last
  // resort, the literal Zitadel sub. Never empty.
  const profile = auth.user?.profile;
  const userName =
    (profile?.name as string | undefined) ??
    (profile?.email as string | undefined) ??
    auth.user?.profile?.sub ??
    '—';

  // Reuse the same React Query key DashboardShell + Landing already use
  // ('my-orgs') so opening this dropdown is free — the cached data drives
  // the org list immediately, no extra request.
  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const orgs = orgsQuery.data ?? [];

  const isDark = theme === 'dark';
  const themeLabel = isDark ? t('theme.switchToLight') : t('theme.switchToDark');

  return (
    <div className="flex h-full flex-col">
      {/* Org + user identity block — sits at the top, mirrors the legacy
          SportAdmin sidebar where the org card opens a switcher. When
          collapsed we render only the initial bubble inside the dropdown
          trigger so the rail stays narrow.
          `h-16` matches the top-bar height in DashboardShell. Use `border-b`
          here (rather than a sibling <Separator /> 1px element) so the line
          lives inside the 64px box — same as the top-bar border — and the
          two lines fall on the exact same pixel row. */}
      <div className="flex h-16 items-center border-b border-border px-3">
        <DropdownMenu>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger
                  className="flex w-full items-center justify-center rounded-xl p-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t('nav.orgMenu')}
                >
                  <OrgInitial name={orgName} logoKey={logoKey} />
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{orgName}</TooltipContent>
            </Tooltip>
          ) : (
            <DropdownMenuTrigger
              className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('nav.orgMenu')}
            >
              <OrgInitial name={orgName} logoKey={logoKey} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-heading text-sm font-semibold text-foreground">
                  {orgName}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {userName}
                </div>
              </div>
              <CaretUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </DropdownMenuTrigger>
          )}
          <DropdownMenuContent align="start" className="w-[260px]">
            {/* User's orgs first, with logo + name. The active org gets a
                lime checkmark on the right so the user can see at a glance
                which one they're currently inside. Clicking jumps directly
                to that org's dashboard root. */}
            {orgs.map((o) => (
              <DropdownMenuItem key={o.id} asChild>
                <Link
                  to={`/orgs/${o.slug}`}
                  onClick={onNavigate}
                  className="flex items-center gap-2.5"
                >
                  <OrgInitial name={o.name} logoKey={o.logo_object_key} className="h-7 w-7 rounded-md text-sm" />
                  <span className="flex-1 truncate font-medium">{o.name}</span>
                  {o.slug === slug && (
                    <Check className="h-4 w-4 shrink-0 text-primary" weight="bold" />
                  )}
                </Link>
              </DropdownMenuItem>
            ))}
            {orgs.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild>
              <Link to="/" onClick={onNavigate}>
                {t('nav.allOrgs')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => void auth.signoutRedirect()}
              className="text-foreground"
            >
              <SignOut className="h-4 w-4" />
              {t('auth.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Primary navigation. Scrolls if it ever overflows; for v1 we have
          two items so this is unlikely. */}
      <div className="flex-1 overflow-y-auto p-3">
        <SidebarNav
          slug={slug}
          collapsed={collapsed}
          recentNav={recentNav}
          onOpenSettings={onOpenSettings}
          onNavigate={onNavigate}
        />
      </div>

      <Separator />

      {/* Footer — theme toggle (mirrors Zitadel's login-page switcher).
          The button is a pill-shaped ≥44px tap target; in collapsed mode it
          centers its icon and shows the label via a hover tooltip. */}
      <div className="p-3">
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={themeLabel}
                className="flex min-h-[var(--tap)] w-full items-center justify-center rounded-pill px-2 py-2 font-heading text-sm font-semibold text-foreground/80 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {isDark ? (
                  <Sun className="h-5 w-5 shrink-0" />
                ) : (
                  <Moon className="h-5 w-5 shrink-0" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{themeLabel}</TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={themeLabel}
            className="flex min-h-[var(--tap)] w-full items-center gap-3 rounded-pill px-3 py-2 font-heading text-sm font-semibold text-foreground/80 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isDark ? (
              <Sun className="h-5 w-5 shrink-0" />
            ) : (
              <Moon className="h-5 w-5 shrink-0" />
            )}
            <span className="truncate">
              {isDark ? t('theme.lightMode') : t('theme.darkMode')}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
